import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { composeApiURL } from "helpers/utility/axiosHelpers";
import {
  Cluster,
  Preview,
  PreviewBorderColor,
} from "../../views/pages/DataPage/CardsView/DataList";
import { useLocation, useNavigate } from "react-router";
import { Option } from "../../views/uikit/FormFields/SelectList";
import { toast } from "react-toastify";
import VLToastComponent from "../../views/components/VLToastComponent";
import { StopOutlined } from "@ant-design/icons";
import { useGetStats } from "./useGetStats";
import AddTagIcon from "assets/icons/AddTagIcon";
import CheckIcon from "assets/icons/FloatIcons/CheckIcon";
import XIcon from "assets/icons/FloatIcons/XIcon";

export interface ReviewData {
  label: string;
  label_category_id: string;
  previews: PreviewData[];
  pending_review_count: number;
  total_count: number;
}

interface PreviewData {
  id: string;
  label: string;
  image_uri: string;
  result: ReviewResult;
  label_category_id: string;
}

type ReviewResult = "CORRECT" | "INCORRECT" | "IGNORE" | "REASSIGN";

export const useReviews = (
  onReviewAction: (sampleId?: string) => void,
  datasetId?: string,
  flowId?: string | null,
  labelId?: string | null,
) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [reviews, setReviews] = useState<Cluster[] | null>(null);
  const [selectedReview, setSelectedReview] = useState<Cluster | null>(null);
  const [error, setError] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [labelsOptions, setLabelsOptions] = useState<Option[]>([]);
  const [isAllReviewed, setIsAllReviewed] = useState(false);
  const { fetchStats, stats } = useGetStats();

  const getBorderColorByResult = useMemo<
    Record<ReviewResult, PreviewBorderColor | undefined>
  >(
    () => ({
      CORRECT: "green",
      INCORRECT: "red",
      IGNORE: undefined,
      REASSIGN: "red",
    }),
    [],
  );

  const getOpacityByResult = useMemo<Record<ReviewResult, number>>(
    () => ({
      CORRECT: 100,
      INCORRECT: 100,
      IGNORE: 50,
      REASSIGN: 100,
    }),
    [],
  );

  const updateReviewItem = useCallback(
    async (
      sampleId: string,
      result: ReviewResult,
      labelCategoryId?: string,
    ) => {
      if (!datasetId || !flowId) return;

      const params = new URLSearchParams();
      params.set("review_result", result);
      if (labelCategoryId) params.set("label_category_id", labelCategoryId);

      const url = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        `/flywheel/${datasetId}/review/${flowId}/${sampleId}?${params.toString()}`,
      );

      try {
        const res = await axios.put<PreviewData>(url);
        const returnedLabelId = res.data.label_category_id;
        onReviewAction(sampleId);

        setSelectedReview((prev) => {
          if (!prev) return prev;

          if (returnedLabelId && returnedLabelId !== prev.id) {
            return {
              ...prev,
              clusterLabel: res.data.label_category_id,
              previews: prev.previews.filter((p) => p.media_id !== sampleId),
            };
          }

          return {
            ...prev,
            clusterLabel: res.data.label_category_id,
            previews: prev.previews.map((p) =>
              p.media_id === sampleId
                ? {
                    ...p,
                    borderColor: getBorderColorByResult[res.data.result],
                    opacity: getOpacityByResult[res.data.result],
                    result: res.data.result,
                    floatingDetails: !res.data.result ? "For review" : undefined,
                    label: res.data.label,
                  }
                : p,
            ),
          };
        });

        fetchStats(datasetId, flowId);
        return res.data;
      } catch (err) {
        console.error("Error updating review item", err);
        setError(err);
      }
    },
    [
      datasetId,
      flowId,
      onReviewAction,
      fetchStats,
      getBorderColorByResult,
      getOpacityByResult,
    ],
  );

  // 🔹 Ref to hold convertPreview
  const convertPreviewRef = useRef(
    (
      p: PreviewData,
      currentLabelsOptions: Option[],
      reviewLabel: string,
    ): Preview => ({
      media_id: p.id,
      image_id: p.id,
      relevanceType: null,
      media_uri: p.image_uri,
      type: "IMAGE" as const,
      title: p.image_uri,
      borderColor: p.result ? getBorderColorByResult[p.result] : undefined,
      opacity: p.result ? getOpacityByResult[p.result] : 100,
      displayIgnorLayout: p.result === "IGNORE",
      floatingDetails: !p.result ? "For review" : undefined,
      label: p.label || reviewLabel,
      actionList: [
        {
          action: {
            text: "decline",
            icon: <XIcon />,
            onClick: () => {
              const toastId = toast(
                <VLToastComponent
                  title="Decline Review"
                  status="ERROR"
                  description={`Are you sure you want to decline this image?`}
                  actionButton={{
                    text: "Decline",
                    type: "primary",
                    onClick: async () => {
                      await updateReviewItem(p.id, "INCORRECT").then((res) => {
                        toast.dismiss(toastId);
                        if (!res) return;
                        const labelToastId = toast(
                          <VLToastComponent
                            title="Decline Review"
                            status="SUCCESS"
                            description={`Image moved to ${res.label}`}
                            onClose={() => toast.dismiss(labelToastId)}
                          />,
                          { autoClose: 7000, closeButton: false, closeOnClick: false },
                        );
                      });
                    },
                  }}
                  onClose={() => toast.dismiss(toastId)}
                />,
                { autoClose: 7000, closeButton: false, closeOnClick: false },
              );
            },
            type: "primary",
            color: "danger",
          },
          tooltip:
            "Reject: This image does not belong to the current class. It will be reassigned to the next-best predicted class and marked for review.",
        },
        {
          action: {
            text: "approve",
            icon: <CheckIcon />,
            onClick: () => updateReviewItem(p.id, "CORRECT"),
            type: "primary",
            color: "success",
          },
          tooltip: "Approve: This image correctly belongs to the current class.",
        },
        {
          action: {
            text: "ignore",
            icon: <StopOutlined />,
            onClick: () => updateReviewItem(p.id, "IGNORE"),
            type: "primary",
            color: "warning",
          },
          tooltip:
            "Ignore: This image doesn't fit any class. It will be excluded from labeling and placed in an 'Unlabeled' group.",
        },
        {
          action: {
            text: "reassign",
            icon: <AddTagIcon />,
            onClick: () => {},
            type: "primary",
            color: "primary",
          },
          subAction: {
            options: currentLabelsOptions,
            onChange: (selectedIds: string[]) => {
              if (selectedIds.length) {
                updateReviewItem(p.id, "REASSIGN", selectedIds.toString());
              }
            },
          },
          tooltip:
            "Assign to another class: Reassign this image to the correct class based on your knowledge.",
        },
      ],
    }),
  );

  useEffect(() => {
    const fetchReviews = async () => {
      if (!datasetId || !flowId) return;
      setLoading(true);
      try {
        const urlAll = composeApiURL(
          process.env.REACT_APP_API_ENDPOINT!,
          `/flywheel/${datasetId}/review/${flowId}`,
        );
        const resAll = await axios.get<ReviewData[]>(urlAll);

        const newLabelsOptions = resAll.data
          .filter((r) => r.label_category_id !== labelId)
          .map((r) => ({ label: r.label, value: r.label_category_id }));
        setLabelsOptions(newLabelsOptions);

        const transformReviewLocal = (review: ReviewData): Cluster => ({
          id: review.label_category_id,
          label: review.label,
          cluster_id: review.label_category_id,
          title: review.total_count + " images",
          floatingDetails: review.pending_review_count + " For review",
          previews: review.previews.map((p) =>
            convertPreviewRef.current(p, newLabelsOptions, review.label),
          ),
          onDetailsClick: (data: Cluster) => {
            const searchParams = new URLSearchParams(location.search);
            searchParams.set("labelId", data.id);
            navigate(`${location.pathname}?${searchParams.toString()}`);
          },
        });

        if (labelId) {
          const urlOne = composeApiURL(
            process.env.REACT_APP_API_ENDPOINT!,
            `/flywheel/${datasetId}/review/${flowId}/${labelId}`,
          );
          const resOne = await axios.get<ReviewData>(urlOne);
          setSelectedReview(transformReviewLocal(resOne.data));
        }

        const transformedReviews = resAll.data.map(transformReviewLocal);
        setReviews(transformedReviews);

        const allCompleted = resAll.data.every((cluster) =>
          cluster.previews.every((p) => !!p.result),
        );
        setIsAllReviewed(allCompleted);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [datasetId, flowId, labelId, navigate, location.pathname, location.search]);

  return {
    reviews,
    selectedReview,
    error,
    loading,
    labelsOptions,
    isAllReviewed,
    stats,
  };
};
